import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  getRoute,
  getRouteShape,
  getRouteShapes,
  listRoutes,
  type ListRoutesParams,
} from '@/lib/api/endpoints';
import { isApiError } from '@/lib/api/error';
import type { ItemEnvelope, ListEnvelope, RouteRead, RouteShapeRead } from '@/types';

const HOUR = 60 * 60 * 1000;

export const routesKeys = {
  all: ['routes'] as const,
  list: (params?: ListRoutesParams) => ['routes', 'list', params ?? {}] as const,
  detail: (id: string) => ['routes', 'detail', id] as const,
  shape: (id: string) => ['routes', 'shape', id] as const,
  shapes: (ids: string[]) => ['routes', 'shapes', ids] as const,
};

export function useRoutes(params?: ListRoutesParams): UseQueryResult<ListEnvelope<RouteRead>> {
  return useQuery({
    queryKey: routesKeys.list(params),
    queryFn: ({ signal }) => listRoutes(params ?? {}, signal),
    staleTime: HOUR,
  });
}

export function useRoute(id: string | undefined): UseQueryResult<ItemEnvelope<RouteRead>> {
  return useQuery({
    queryKey: routesKeys.detail(id ?? ''),
    queryFn: ({ signal }) => getRoute(id ?? '', signal),
    enabled: Boolean(id),
    staleTime: HOUR,
  });
}

/**
 * Fetch polyline geometry for many routes in one request. Routes without
 * shape data are simply absent from the result. The query key sorts the ids
 * so it stays cache-stable regardless of the caller's ordering.
 */
export function useRouteShapes(routeIds: string[]): UseQueryResult<RouteShapeRead[]> {
  const ids = [...routeIds].sort();
  return useQuery({
    queryKey: routesKeys.shapes(ids),
    queryFn: async ({ signal }): Promise<RouteShapeRead[]> => {
      const env = await getRouteShapes(ids, signal);
      return env.data.shapes;
    },
    enabled: ids.length > 0,
    staleTime: HOUR,
  });
}

/**
 * Fetch the polyline geometry for a single route. Treats a 404 as "no shape
 * available yet" rather than an error, so the layer just renders nothing
 * for missing routes (e.g. before the backend ships the endpoint).
 */
export function useRouteShape(routeId: string | undefined): UseQueryResult<RouteShapeRead | null> {
  return useQuery({
    queryKey: routesKeys.shape(routeId ?? ''),
    queryFn: async ({ signal }): Promise<RouteShapeRead | null> => {
      try {
        const env = await getRouteShape(routeId ?? '', signal);
        return env.data;
      } catch (err) {
        if (isApiError(err) && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: Boolean(routeId),
    staleTime: HOUR,
    retry: (failureCount, err) => {
      if (isApiError(err) && err.status === 404) return false;
      return failureCount < 1;
    },
  });
}
